import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import WbSunnyTwoToneIcon from '@mui/icons-material/WbSunnyTwoTone';
import CloudTwoToneIcon from '@mui/icons-material/CloudTwoTone';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ThunderstormTwoToneIcon from '@mui/icons-material/ThunderstormTwoTone';
import WaterDropTwoToneIcon from '@mui/icons-material/WaterDropTwoTone';
import { CircularProgress } from '@mui/material';
import { getWeather } from '../../api/weather';
import { useTheme } from '@mui/material/styles';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface WeatherProps {
  city: string;
  compact?: boolean;
}

// Adapter l'interface pour min/max
interface DailyWeatherData {
  date: string;
  min: number;
  max: number;
  description: string;
}

const WeatherSlide: React.FC<WeatherProps> = ({ city, compact }) => {
  const [dailyWeatherData, setDailyWeatherData] = useState<DailyWeatherData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const fetchWeather = async () => {
      setIsLoading(true);
      try {
        const data = await getWeather(city);
        // Regrouper les prévisions par jour
        const daysMap: { [date: string]: { temps: number[]; descriptions: string[] } } = {};
        data.list.forEach((item: any) => {
          const date = new Date(item.dt * 1000);
          // Format YYYY-MM-DD pour regrouper
          const dayKey = date.toISOString().split('T')[0];
          if (!daysMap[dayKey]) daysMap[dayKey] = { temps: [], descriptions: [] };
          daysMap[dayKey].temps.push(item.main.temp);
          daysMap[dayKey].descriptions.push(item.weather[0].description);
        });
        // Prendre les 7 prochains jours
        const days = Object.keys(daysMap).slice(0, 7);
        const dailyData: DailyWeatherData[] = days.map((dayKey) => {
          const temps = daysMap[dayKey].temps;
          const descriptions = daysMap[dayKey].descriptions;
          // Min et max température
          const min = Math.round(Math.min(...temps));
          const max = Math.round(Math.max(...temps));
          // Description la plus fréquente
          const descFreq: { [desc: string]: number } = {};
          descriptions.forEach(desc => { descFreq[desc] = (descFreq[desc] || 0) + 1; });
          let mainDesc = Object.entries(descFreq).sort((a, b) => b[1] - a[1])[0][0];
          // Mettre la première lettre en majuscule
          mainDesc = mainDesc.charAt(0).toUpperCase() + mainDesc.slice(1);
          // Format jour de la semaine + date
          const dateObj = new Date(dayKey);
          const dayName = dateObj.toLocaleDateString('fr-FR', { weekday: 'long' });
          const dayNum = dateObj.getDate().toString().padStart(2, '0');
          const monthNum = (dateObj.getMonth() + 1).toString().padStart(2, '0');
          const fullDay = `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNum}/${monthNum}`;
          return {
            date: fullDay,
            min,
            max,
            description: mainDesc,
          };
        });
        setDailyWeatherData(dailyData);
        setError(null);
      } catch (error) {
        setError('Unable to fetch weather data. Please check the API key.');
      } finally {
        setIsLoading(false);
      }
    };

    if(!city) {
      setIsLoading(false);
      return;
    }
    fetchWeather();
  }, [city]);

  const renderWeatherIcon = (description: string) => {
    const desc = (description || '').toLowerCase();
    if (desc.includes('soleil') || desc.includes('clear')) {
      return <WbSunnyTwoToneIcon fontSize='large' />;
    } else if (desc.includes('nuage') || desc.includes('couvert')) {
      return <CloudTwoToneIcon fontSize='large' />;
    } else if (desc.includes('neige')) {
      return <AcUnitIcon fontSize='large' />;
    } else if (desc.includes('orage') || desc.includes('tonnerre') || desc.includes('tempête')) {
      return <ThunderstormTwoToneIcon fontSize='large' />;
    } else if (desc.includes('pluie') || desc.includes('rain')) {
      return <WaterDropTwoToneIcon fontSize='large' />;
    } else {
      return <WbSunnyTwoToneIcon fontSize='large' />;
    }
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  // compact header mode: show only the first day's brief summary
  if (compact) {
    const day = dailyWeatherData[0];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isLoading ? (
          <CircularProgress size={20} />
        ) : error ? (
          <span className='text-sm font-medium' style={{color: theme.palette.error.main}}>{error}</span>
        ) : day ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ textAlign: 'right', minWidth: 140 }}>
              <div className='text-sm font-semibold' style={{ lineHeight: 1 }}>{day.date}</div>
              <div className='text-xs' style={{color: theme.palette.text.primary}} >Min: {day.min}° / Max: {day.max}°</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <div>{renderWeatherIcon(day.description)}</div>
              <div className='text-xs' style={{color: theme.palette.text.primary}} >{day.description}</div>
            </div>
          </div>
        ) : (
          <span className='text-sm' style={{color: theme.palette.text.primary}} >Pas de données</span>
        )}
      </div>
    );
  }

  return (
    <div className='weather-container' style={{ maxWidth: 400, margin: '0 auto' }}>
      {isLoading ? (
        <CircularProgress />
      ) : error ? (
        <span className='text-xl font-bold ' style={{color: theme.palette.error.main}}>{error}</span>
      ) : (
        <Slider {...settings}>
          {dailyWeatherData.map((day, index) => (
            <div key={index} className='weather-slide' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 120, marginBottom: 10, width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%' }}>
                <span className='text-xl font-bold' style={{ letterSpacing: 1, textAlign: 'center', minWidth: 120 }}>{day.date}</span>
                <span className='text-lg font-semibold' style={{ textAlign: 'center', minWidth: 90 }}>{day.description}</span>
                <span style={{ textAlign: 'center', minWidth: 40 }}>{renderWeatherIcon(day.description)}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%' }}>
                <span className='text-lg font-semibold mb-1'>Min : {day.min}°C</span>
                <span className='text-lg font-semibold mb-1'>/</span>
                <span className='text-lg font-semibold mb-1'>Max : {day.max}°C</span>
              </div>
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export default WeatherSlide;